/**
 * Box 5 of the IAP cover: what the plan carries.
 *
 * Three kinds of thing share the one box. The checkboxes pick which ICS forms are merged; the attached files are
 * documents from File Management whose pages are appended to the end of the plan; the free-text rows name something
 * that travels outside the packet entirely. The printed sheet has ten "Other Attachments" rows, and files and labels
 * share them, which is why the budget is tracked across both.
 *
 * Saved on commit rather than per toggle: every incident info write appends a new version, and the period resolver
 * only looks back a limited number of versions.
 */

import React, { useMemo, useState } from 'react';
import { Paperclip, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import IapAttachmentSelectionList from 'components/IAP/IapAttachmentSelectionList';
import {
  areManifestsEqual,
  IAP_FAMILY_CAPABILITIES,
  resolveIapAttachmentManifest,
  type IapAttachmentManifest,
} from 'components/IAP/iapAttachmentManifest';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import IapFileAttachmentPicker from './IapFileAttachmentPicker';

type IapCoverAttachmentsBoxProps = {
  disabled?: boolean;
};

const IapCoverAttachmentsBox: React.FC<IapCoverAttachmentsBoxProps> = ({
  disabled = false,
}) => {
  const incidentId = useActualIncidentId();
  const { data: incidentInfo } = useIncidentInfo();
  const incidentMutation = useUpdateIcsInfo();
  const family = useIcsFormFamily();
  const capability = IAP_FAMILY_CAPABILITIES[family];

  const savedManifest = useMemo(
    () =>
      resolveIapAttachmentManifest(incidentInfo?.iapAttachmentManifest, family),
    [incidentInfo?.iapAttachmentManifest, family]
  );

  const [draft, setDraft] = useState<IapAttachmentManifest | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const manifest = draft ?? savedManifest;
  const isDirty = draft !== null && !areManifestsEqual(draft, savedManifest);

  const usedRows =
    manifest.fileAttachments.length + manifest.otherAttachments.length;
  const rowsRemaining = capability.otherAttachmentLimit - usedRows;

  const handleSave = async () => {
    if (!draft) return;
    try {
      await incidentMutation.mutateAsync({
        ...incidentInfo,
        iapAttachmentManifest: draft,
      });
      setDraft(null);
      toast.success('IAP attachments saved.');
    } catch {
      // useUpdateIcsInfo surfaces the failure; keep the draft so edits survive.
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <IapAttachmentSelectionList
        manifest={manifest}
        family={family}
        disabled={disabled}
        onChange={setDraft}
      />

      <section className="tw:flex tw:flex-col tw:gap-2">
        <h5 className="tw:m-0 tw:text-sm tw:font-semibold">
          Other Attachments
        </h5>

        {manifest.fileAttachments.length > 0 && (
          <ul className="tw:flex tw:flex-col tw:gap-1">
            {manifest.fileAttachments.map((attachment) => (
              <li
                key={attachment.fileId}
                className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
                data-testid="iap-cover-file-attachment"
              >
                <Paperclip className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
                <span className="tw:min-w-0 tw:flex-1 tw:truncate">
                  {attachment.label}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  aria-label={`Remove ${attachment.label}`}
                  onClick={() =>
                    setDraft({
                      ...manifest,
                      fileAttachments: manifest.fileAttachments.filter(
                        (candidate) => candidate.fileId !== attachment.fileId
                      ),
                    })
                  }
                >
                  <Trash2 className="tw:size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {manifest.otherAttachments.map((label, index) => (
          <div
            key={index}
            className="tw:flex tw:max-w-lg tw:items-center tw:gap-2"
          >
            <Input
              placeholder="Name an attachment..."
              disabled={disabled}
              value={label}
              onChange={(event) => {
                const next = [...manifest.otherAttachments];
                next[index] = event.target.value;
                setDraft({ ...manifest, otherAttachments: next });
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label={`Remove attachment ${index + 1}`}
              onClick={() =>
                setDraft({
                  ...manifest,
                  otherAttachments: manifest.otherAttachments.filter(
                    (_, at) => at !== index
                  ),
                })
              }
            >
              <Trash2 className="tw:size-4" />
            </Button>
          </div>
        ))}

        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <Button
            variant="outline"
            disabled={disabled || !incidentId}
            onClick={() => setPickerOpen(true)}
          >
            <Paperclip className="tw:size-4" />
            Attach files
          </Button>
          <Button
            variant="outline"
            disabled={disabled || rowsRemaining <= 0}
            onClick={() =>
              setDraft({
                ...manifest,
                otherAttachments: [...manifest.otherAttachments, ''],
              })
            }
          >
            <Plus className="tw:size-4" />
            Add label
          </Button>
          <span className="tw:text-xs tw:text-muted-foreground">
            {rowsRemaining > 0
              ? `${rowsRemaining} of ${capability.otherAttachmentLimit} rows left`
              : `The ${family.toUpperCase()} cover prints at most ${capability.otherAttachmentLimit} attachments.`}
          </span>
        </div>
      </section>

      {isDirty && (
        <div className="tw:flex tw:items-center tw:gap-2">
          <Button
            disabled={disabled || incidentMutation.isPending}
            onClick={() => void handleSave()}
          >
            Save attachments
          </Button>
          <Button
            variant="outline"
            disabled={incidentMutation.isPending}
            onClick={() => setDraft(null)}
          >
            Discard
          </Button>
        </div>
      )}

      {incidentId && (
        <IapFileAttachmentPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          incidentId={incidentId}
          selected={manifest.fileAttachments}
          // The free-text rows the user has already written are not up for
          // grabs, so the picker's budget is what is left after them.
          maxSelectable={Math.max(
            capability.otherAttachmentLimit - manifest.otherAttachments.length,
            0
          )}
          onConfirm={(fileAttachments) =>
            setDraft({ ...manifest, fileAttachments })
          }
        />
      )}
    </div>
  );
};

export default IapCoverAttachmentsBox;
